import type {
  TemplateChoiceDto,
  TemplateDayDto,
  TemplateDecisionClass,
  TemplateDecisionContextDto,
  TemplateScheduleDto,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3] A DECISION, AND A SUGGESTION, IN WORDS — pure, so every class is tested without a render.
 *
 *  ⚠️ AN ID IS NEVER SHOWN AS DATA ([ANALYTICS §8]'s rule): an org unit, a post type, a vehicle type
 *  and a policy are named from the masters the page already read, and an id none of them resolves
 *  reads as "no longer available" — a choice the server's `problems` will also be reporting.
 *
 *  ⚠️ `SKIP` MEANS A DIFFERENT THING IN EACH CLASS, which is why the class is the first argument:
 *  a skipped SOURCE leaves its force rows out of the order, a skipped COLUMN leaves the column out, a
 *  skipped DUTY POINT leaves the duty point out. One word for all three would hide which people go.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type DecisionLookups = {
  orgUnitName: (id: string) => string | undefined;
  postTypeName: (id: string) => string | undefined;
  vehicleTypeName: (id: string) => string | undefined;
  policyName: (id: string) => string | undefined;
  days: readonly TemplateDayDto[];
};

export const UNAVAILABLE = 'no longer available';

/** 24-hour `HH:mm`, as the booklet and the shift fields both write it. */
const time = (value: string): string => value;

export const scheduleWords = (schedule: TemplateScheduleDto, days: readonly TemplateDayDto[]): string => {
  const names = schedule.dayKeys.map((key) => days.find((day) => day.key === key)?.name ?? key);
  const timing =
    schedule.timing.kind === 'WHOLE_DAY'
      ? schedule.timing.reportingTime === null
        ? 'whole day'
        : `whole day, report at ${time(schedule.timing.reportingTime)}`
      : schedule.timing.shifts.map((shift) => `${time(shift.start)}–${time(shift.end)}`).join(', ');
  return `${names.join(', ')} · ${timing}${schedule.nextCalendarDay ? ' · on the next calendar day' : ''}`;
};

export const choiceWords = (
  decisionClass: TemplateDecisionClass,
  choice: TemplateChoiceDto,
  context: TemplateDecisionContextDto,
  lookups: DecisionLookups,
): string => {
  switch (choice.choice) {
    case 'ORG_UNIT':
      return lookups.orgUnitName(choice.orgUnitId) ?? `A unit ${UNAVAILABLE}`;
    case 'POST_TYPE':
      return `Post type: ${lookups.postTypeName(choice.postTypeId) ?? UNAVAILABLE}`;
    case 'VEHICLE_TYPE':
      return `Vehicle type: ${lookups.vehicleTypeName(choice.vehicleTypeId) ?? UNAVAILABLE}`;
    case 'ONE_OFF':
      if ('restrictToPostTypeIds' in choice) {
        const restricted = choice.restrictToPostTypeIds.map((id) => lookups.postTypeName(id) ?? UNAVAILABLE);
        return restricted.length === 0
          ? `One-off column “${choice.label}”`
          : `One-off column “${choice.label}”, filled by ${restricted.join(', ')}`;
      }
      return `One-off role “${choice.label}”`;
    case 'POLICY':
      return `Policy: ${lookups.policyName(choice.policyId) ?? UNAVAILABLE}`;
    case 'ROLE': {
      const role =
        context.class === 'IN_CHARGE_ROLE' ? context.roles.find((each) => each.id === choice.mappedRole) : undefined;
      return `Role: ${role?.label ?? UNAVAILABLE}`;
    }
    case 'DROP':
      return 'Dropped — no in-charge entry';
    case 'AS_READ':
      return 'The rows as read';
    case 'PRINTED':
      return `The printed total — ${String(choice.rows.length)} row${choice.rows.length === 1 ? '' : 's'} corrected`;
    case 'HOLD':
      return 'The template is held back';
    case 'SCHEDULE':
      return scheduleWords(choice.schedule, lookups.days);
    case 'PLACE':
      return `Place: ${choice.place}`;
    case 'SKIP':
      return decisionClass === 'SOURCE'
        ? 'Left out — its force rows are not in the order'
        : decisionClass === 'FORCE_COLUMN'
          ? 'Left out — the column is not in the order'
          : 'Left out — the duty point is not built';
  }
};
